// ============================================================
// 16. SlowMovingProductsPage.tsx
// ============================================================
// src/features/reports/pages/SlowMovingProductsPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getSlowMovingProducts, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    Snail, Package, DollarSign, AlertTriangle,
    TrendingDown, Clock, Archive, BarChart3
} from 'lucide-react';
import { motion } from 'framer-motion';

export function SlowMovingProductsPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState<'all' | 'zero_sales'>('all');

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-slow-moving', tenantId, params],
        queryFn: () => getSlowMovingProducts(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'slow_moving', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const products = data?.products || [];
    const filteredProducts = viewMode === 'zero_sales'
        ? products.filter((p: any) => p.quantitySold === 0)
        : products;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/30">
            <ReportHeader
                title="Slow Moving Products"
                description="Low turnover inventory analysis"
                icon={Snail}
                iconGradient="from-orange-500 to-amber-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || products.length === 0 ? (
                    <EmptyState
                        icon={<TrendingDown className="w-10 h-10" />}
                        title="No Slow Moving Products"
                        description="All products are selling well. Great inventory management!"
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Slow Moving Items"
                                value={String(data.summary?.totalSlowMoving || 0)}
                                icon={Snail}
                                gradient="from-orange-500 to-amber-600"
                            />
                            <SummaryCard
                                label="Zero Sales"
                                value={String(data.summary?.zeroSales || 0)}
                                icon={AlertTriangle}
                                gradient="from-red-500 to-rose-600"
                                subtitle={`${((data.summary?.zeroSales || 0) / (data.summary?.totalSlowMoving || 1) * 100).toFixed(0)}% of slow movers`}
                            />
                            <SummaryCard
                                label="Stock Value at Risk"
                                value={formatNaira(data.summary?.totalStockValue || 0)}
                                icon={DollarSign}
                                gradient="from-slate-500 to-slate-600"
                            />
                            <SummaryCard
                                label="Avg Turnover Rate"
                                value={`${(products.reduce((s: number, p: any) => s + parseFloat(p.turnoverRate), 0) / products.length).toFixed(1)}%`}
                                icon={BarChart3}
                                gradient="from-blue-500 to-indigo-600"
                            />
                        </div>

                        {/* View Toggle */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setViewMode('all')}
                                className={`px-4 py-2 text-sm font-medium rounded-lg ${viewMode === 'all' ? 'bg-orange-50 text-orange-700 border border-orange-200' : 'text-slate-500 border border-slate-200'
                                    }`}
                            >
                                All Slow Moving ({products.length})
                            </button>
                            <button
                                onClick={() => setViewMode('zero_sales')}
                                className={`px-4 py-2 text-sm font-medium rounded-lg ${viewMode === 'zero_sales' ? 'bg-red-50 text-red-700 border border-red-200' : 'text-slate-500 border border-slate-200'
                                    }`}
                            >
                                Zero Sales ({data.summary?.zeroSales || 0})
                            </button>
                        </div>

                        {/* Stock Value Risk Visualization */}
                        <Card>
                            <CardContent className="p-5">
                                <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                                    Stock Value at Risk (Top 10)
                                </h3>
                                <div className="space-y-3">
                                    {products.slice(0, 10).map((product: any, i: number) => {
                                        const maxValue = parseFloat(products[0]?.stockValue || '1');
                                        const percentage = (parseFloat(product.stockValue) / maxValue * 100);

                                        return (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className="text-xs text-slate-600 w-32 truncate">{product.productName}</span>
                                                <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${percentage}%` }}
                                                        transition={{ duration: 0.5, delay: i * 0.03 }}
                                                        className={`h-full rounded-full ${product.quantitySold === 0
                                                                ? 'bg-gradient-to-r from-red-500 to-rose-600'
                                                                : parseFloat(product.turnoverRate) < 10
                                                                    ? 'bg-gradient-to-r from-orange-500 to-amber-600'
                                                                    : 'bg-gradient-to-r from-yellow-500 to-amber-500'
                                                            }`}
                                                    />
                                                </div>
                                                <span className="text-xs font-bold text-slate-700 w-24 text-right">
                                                    {formatNaira(product.stockValue)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Product</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Category</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Stock Qty</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Sold Qty</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Turnover Rate</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cost Price</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Stock Value</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {filteredProducts.map((product: any, i: number) => {
                                            const turnoverRate = parseFloat(product.turnoverRate);
                                            const isDead = product.quantitySold === 0;

                                            return (
                                                <tr key={i} className={`hover:bg-slate-50 transition-colors ${isDead ? 'bg-red-50/20' : ''
                                                    }`}>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`p-2 rounded-lg ${isDead ? 'bg-red-100' :
                                                                    turnoverRate < 10 ? 'bg-orange-100' : 'bg-yellow-100'
                                                                }`}>
                                                                {isDead ? (
                                                                    <Archive className="w-4 h-4 text-red-600" />
                                                                ) : (
                                                                    <Clock className="w-4 h-4 text-orange-600" />
                                                                )}
                                                            </div>
                                                            <span className="text-sm font-medium text-slate-900">{product.productName}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">{product.categoryName}</td>
                                                    <td className="px-6 py-4 text-right text-sm font-semibold">
                                                        {formatNumber(product.quantityOnHand)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <span className={`text-sm font-bold ${isDead ? 'text-red-600' : 'text-slate-600'
                                                            }`}>
                                                            {formatNumber(product.quantitySold)}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge
                                                            variant={isDead ? 'danger' : turnoverRate < 10 ? 'warning' : 'neutral'}
                                                            size="sm"
                                                            pulse={isDead}
                                                        >
                                                            {product.turnoverRate}%
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                        {formatNaira(product.costPrice)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-amber-600">
                                                        {formatNaira(product.stockValue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge
                                                            variant={isDead ? 'danger' : turnoverRate < 5 ? 'warning' : 'info'}
                                                            size="sm"
                                                        >
                                                            {isDead ? 'DEAD STOCK' : turnoverRate < 5 ? 'CRITICAL' : 'SLOW'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}